import { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import api, { onServerReachability } from "@/lib/api";
import { setYardTz } from "@/lib/datetime";
import { hydratePrinterConfig } from "@/lib/printerMap";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = authenticated
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [directory, setDirectory] = useState({});
  // "can't reach the server" is NOT "you are logged out" (NG-2). A gateway hiccup, an origin
  // restart or a dropped tablet connection must never be reported as a dead session — that is
  // what dumped the crew at a login screen with no explanation.
  const [unreachable, setUnreachable] = useState(false);
  // R-10. A THIRD FACT, BECAUSE IT IS NEITHER OF THE OTHER TWO. The server ANSWERED — so it is
  // not unreachable — but it answered with a 500, so it never said whether the session is good.
  // That is "we could not check", and it gets its own state and its own words. Conflating it
  // with "logged out" is what dumped the crew at a login screen every time a worker died.
  const [sessionUnverified, setSessionUnverified] = useState(false);
  const userRef = useRef(user);
  userRef.current = user;
  const probeTimer = useRef(null);
  const verifyTries = useRef(0);
  const verifyTimer = useRef(null);
  const refreshRef = useRef(null);

  const loadDirectory = async () => {
    try { const { data } = await api.get("/users/directory"); setDirectory(data.directory || {}); }
    catch { setDirectory({}); }
  };

  const loadPermissions = async () => {
    try { const { data } = await api.get("/me/permissions"); setPermissions(data.permissions || {}); return data.permissions || {}; }
    catch { setPermissions({}); return {}; }
  };

  // R-10. THE SESSION CHECK FAILED WITHOUT THE SERVER SAYING 401. Retried a BOUNDED number of
  // times with backoff and jitter — an unbounded loop would turn one bad deploy into a retry
  // storm across every tablet in the yard — and then it stops and leaves an honest screen with
  // a Try again on it. Never a spinner that hides an outage, never a login form that lies.
  const VERIFY_TRIES = 3;
  const stopVerifyRetry = () => { if (verifyTimer.current) { clearTimeout(verifyTimer.current); verifyTimer.current = null; } };
  const onVerifyFailed = useCallback(() => {
    // ALREADY SIGNED IN: a failed re-check never erases a user. The cookie is still in the
    // browser and the server never disowned it.
    if (userRef.current) return;
    if (verifyTries.current < VERIFY_TRIES) {
      const n = verifyTries.current++;
      stopVerifyRetry();
      verifyTimer.current = setTimeout(() => refreshRef.current?.(),
                                       Math.min(8000, 1000 * 2 ** n) + Math.floor(Math.random() * 400));
      return;
    }
    setSessionUnverified(true);
  }, []);

  const refresh = useCallback(async () => {
    // SEC-09: the httpOnly session cookie decides — there is no client-side token to check.
    try {
      const { data } = await api.get("/auth/me");
      setYardTz(data.yard_timezone);
      await loadPermissions();
      loadDirectory();
      // Printer names + stock assignments are server-side: pull them on EVERY session boot
      // so no screen on any device can fall back to showing a bare printer serial.
      hydratePrinterConfig();
      verifyTries.current = 0; stopVerifyRetry(); setSessionUnverified(false);
      setUser(data);
    } catch (err) {
      // ONLY THE SERVER SAYING 401 ENDS A SESSION (R-10). The line that used to be here read
      // `if (err?.serverUnreachable) return; setUser(false);` — WIDER than the rule its own
      // comment cited, so the plain `text/plain "Internal Server Error"` this app answers for
      // any unhandled exception was read as "your session is over". An OOM worker restart
      // therefore looked like a mass logout. A 500 says nothing about the cookie the browser
      // is still holding; only a 401 does.
      if (err?.response?.status === 401) {
        stopVerifyRetry(); setSessionUnverified(false);
        setUser(false); setPermissions({});
        return;
      }
      if (err?.serverUnreachable) return;   // the amber bar and the /version probe own this one
      onVerifyFailed();
    }
  }, [onVerifyFailed]);
  refreshRef.current = refresh;

  // ONE retry loop for the whole app. Any successful response — this probe or a normal
  // user action — flips reachability back through the api interceptor, so the bar clears
  // itself with no refresh and no dismiss button. /version is the public build probe.
  const stopProbe = () => { if (probeTimer.current) { clearTimeout(probeTimer.current); probeTimer.current = null; } };
  const probe = useCallback(async (attempt = 0) => {
    stopProbe();
    try {
      await api.get("/version");
      if (!userRef.current) refresh();      // boot case: finish authenticating now it's back
    } catch (err) {
      if (err?.serverUnreachable) {
        probeTimer.current = setTimeout(() => probe(attempt + 1), Math.min(20000, 1500 * 2 ** Math.min(attempt, 4)));
      }
    }
  }, [refresh]);

  useEffect(() => {
    const off = onServerReachability((ok) => {
      setUnreachable(!ok);
      if (ok) stopProbe(); else probe(0);
    });
    refresh();
    return () => { off(); stopProbe(); stopVerifyRetry(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (userObj) => {
    // SEC-09: the session cookie was set by verify-totp; nothing is stored client-side.
    await loadPermissions();
    loadDirectory();
    hydratePrinterConfig(true);   // fresh device/tablet: printer names ready before first print
    // Fetch the full profile (incl. preferred_surface) so post-login landing can honor
    // the tech's saved Desk/Floor surface. Falls back to the login payload if it fails.
    try {
      const { data } = await api.get("/auth/me");
      setYardTz(data.yard_timezone);
      setUser(data);
    } catch { setUser(userObj); }
  };

  // Desk vs Floor surface — saved SERVER-SIDE per user (never localStorage).
  const setSurface = async (surface) => {
    await api.put("/me/preferred-surface", { surface });
    setUser((u) => (u ? { ...u, preferred_surface: surface } : u));
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    try { sessionStorage.removeItem("floor_roundtrip"); } catch { /* ignore */ }
    setUser(false); setPermissions({});
  };

  const has = (cap) => !!user && (user.role === "Admin" || !!permissions[cap]);
  // email → display name (falls back to the email for legacy / unresolved actors)
  const resolveName = (email) => (email && directory[email]) || email || "—";

  // ONE Try again, and the screen that is showing decides what it retries: "can't reach the
  // server" is a reachability probe, "couldn't check your session" is the session check itself.
  const retryNow = () => {
    if (sessionUnverified) {
      verifyTries.current = 0; setSessionUnverified(false);
      refresh();
      return;
    }
    probe(0);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, permissions, has, login, logout, refresh, directory, resolveName, setSurface, unreachable, sessionUnverified, retryNow }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
